import { gql } from "@apollo/client";

export const EXPORT_ORDER_DATA = gql`
  mutation ExportedOrderData(
    $name: String!
    $format: String!
    $filterOptions: OrdersFilterOptionsInput!
  ) {
    exportedOrderData(
      name: $name
      format: $format
      filterOptions: $filterOptions
    ) {
      successMessage
    }
  }
`;

export const FETCH_ORDER_SUMMARY_GRAPH = gql`
  query FetchOrderSummaryGraph(
    $dateRange: DateRangeInput!
    $granularity: String!
    $filterOptions: OrdersGraphFilterOptionsInput
  ) {
    me {
      orderSummaryGraph(
        dateRange: $dateRange
        granularity: $granularity
        filterOptions: $filterOptions
      ) {
        data {
          orderRevenue
          orderCount
          datetime
        }
        queryMetadata {
          bqLogs {
            query
            queryParameters
            queryTypes
            labels
            jobId
            queryId
            executionTimestamp
          }
        }
      }
    }
  }
`;

export const FETCH_ORDER_SUMMARY_GRAPH_KPI = gql`
  query FetchOrderSummaryGraphKPI(
    $dateRange: DateRangeInput!
    $comparedDateRange: DateRangeInput!
    $filterOptions: OrdersGraphFilterOptionsInput
  ) {
    me {
      orderSummaryGraphKPI(
        dateRange: $dateRange
        comparedDateRange: $comparedDateRange
        filterOptions: $filterOptions
      ) {
        currentKPIs {
          orderRevenue
          orderCount
        }
        comparisonKPIs {
          orderRevenue
          orderCount
        }
        queryMetadata {
          bqLogs {
            query
            queryParameters
            queryTypes
            labels
            jobId
            queryId
            executionTimestamp
          }
        }
      }
    }
  }
`;

export const FETCH_ORDER_SUMMARY = gql`
  query FetchOrderSummary(
    $filterOptions: OrdersFilterOptionsInput!
    $sorting: OrdersSortingInput
    $offset: Int
    $limit: Int
  ) {
    me {
      orders(
        filterOptions: $filterOptions
        sorting: $sorting
        offset: $offset
        limit: $limit
      ) {
        queryMetadata {
          bqLogs {
            query
            queryParameters
            queryTypes
            labels
            jobId
            queryId
            executionTimestamp
          }
        }
        data {
          conversionEventId
          orderId
          occurredAt
          orderType
          orderNumber
          revenueInDollars
          discountValue
          shippingValue
          taxValue
          attributed
          orderTags
          sourceName
          customerTags
          discountCodes
          customerId
          customerEmail
          refundAmountInDollars
          numberOfTouchpoints
          newNumberOfTouchpoints
          subscriptionType
          products {
            title
            quantity
          }
        }
        totalCount
      }
    }
  }
`;

export const FETCH_ORDER_TAG_OPTIONS = gql`
  query FetchOrderTagOptions($dateRange: DateRangeInput!) {
    me {
      orderTags(dateRange: $dateRange)
    }
  }
`;
export const FETCH_SOURCE_NAME_OPTIONS = gql`
  query FetchSourceNameOptions($dateRange: DateRangeInput!) {
    me {
      sourceNames(dateRange: $dateRange)
    }
  }
`;
export const FETCH_CUSTOMER_TAG_OPTIONS = gql`
  query FetchCustomerTagOptions($dateRange: DateRangeInput!) {
    me {
      customerTags(dateRange: $dateRange)
    }
  }
`;
export const FETCH_DISCOUNT_CODES_OPTIONS = gql`
  query FetchOrderDiscountCodeOptions($dateRange: DateRangeInput!) {
    me {
      discountCodes(dateRange: $dateRange)
    }
  }
`;
export const FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS = gql`
  query FetchOrderPlatformAndEcommerceOptions {
    me {
      adPlatforms
      ecommercePlatforms
    }
  }
`;
export const FETCH_ORDER_TYPE_OPTIONS = gql`
  query FetchOrderTypeOptions($dateRange: DateRangeInput!) {
    me {
      orderTypes(dateRange: $dateRange)
    }
  }
`;
export const FETCH_ORDER_PRODUCT_OPTIONS = gql`
  query FetchOrderProductOptions($dateRange: DateRangeInput!) {
    me {
      products(dateRange: $dateRange)
    }
  }
`;

export const FETCH_TOUCHPOINTS_BY_ATTRIBUTION_MODE_WINDOW_AND_MODEL = gql`
  query FetchTouchpointsByAttributionModeWindowAndModel(
    $conversionEventId: String!
    $accountingMode: String
    $attributionWindowDays: String
    $attributionModel: String
  ) {
    me {
      touchpointsByAttributionModeWindowAndModel(
        conversionEventId: $conversionEventId
        accountingMode: $accountingMode
        attributionWindowDays: $attributionWindowDays
        attributionModel: $attributionModel
      ) {
        touchpoints {
          conversionEventId
          eventTs
          campaignName
          adsetName
          adName
          kind
          transactionValue
          platform
          adKeyHash
          platformNorthbeamLabelValue
        }
        isProcessed
        queryMetadata {
          bqLogs {
            query
            queryParameters
            queryTypes
          }
        }
      }
    }
  }
`;

export const FETCH_ORDER = gql`
  query FetchOrder($orderNumber: String!, $showPriorNonConversions: Boolean) {
    me {
      order(
        orderNumber: $orderNumber
        showPriorNonConversions: $showPriorNonConversions
      ) {
        conversionEventId
        orderId
        occurredAt
        orderType
        orderNumber
        customerId
        customerEmail
        customerFirstName
        products {
          title
          priceInDollars
          quantity
        }
        subscriptionType
        discountCodes
        revenueInDollars
        discountValue
        shippingValue
        taxValue
        refundAmountInDollars
        priorNonConversions
        shippingAddress1
        shippingAddress2
        shippingCity
        shippingProvince
        shippingProvinceCode
        shippingCountry
        shippingCountryCode
        shippingZip
        customerPhone
        priorNonConversionsKeyNameMapping {
          campaignMap
          adsetMap
          adMap
        }
      }
    }
  }
`;

export const FETCH_SANITIZED_CONNECTIONS_BY_TYPE = gql`
  query FetchSanitizedConnectionsByType($type: ConnectionType) {
    me {
      id
      sanitizedConnections(type: $type) {
        id
        clientId
        type
        data
        connectionValidation
        allProfiles
      }
    }
  }
`;

export const UPSERT_CONNECTION = gql`
  mutation UpsertConnection(
    $id: ID
    $type: ConnectionType!
    $data: JSONObject!
  ) {
    upsertConnection(data: $data, type: $type, id: $id) {
      errorMessage
      connection {
        id
        clientId
        type
        data
        connectionValidation
      }
    }
  }
`;

export const SOFT_DELETE_CONNECTION = gql`
  mutation SoftDeleteConnection($id: ID!, $type: ConnectionType!) {
    softDeleteConnection(id: $id, type: $type) {
      errorMessage
      connection {
        id
        clientId
        type
        data
        connectionValidation
      }
    }
  }
`;

export const DELETE_CONNECTION = gql`
  mutation DeleteConnection($id: ID!, $type: ConnectionType!) {
    deleteConnection(id: $id, type: $type) {
      errorMessage
      connection {
        id
        clientId
        type
        data
        connectionValidation
      }
    }
  }
`;

export const ATTEMPT_TO_ADD_ACCOUNT_MANAGER = gql`
  mutation AttemptToAddAccountManager($email: String!) {
    attemptToAddAccountManager(email: $email)
  }
`;

export const ATTEMPT_TO_REMOVE_ACCOUNT_MANAGER = gql`
  mutation AttemptToRemoveAccountManager($managerId: String!) {
    attemptToRemoveAccountManager(managerId: $managerId)
  }
`;

export const UPDATE_USER_DETAILS = gql`
  mutation UpdateUserDetails(
    $companyName: String!
    $firstName: String!
    $lastName: String!
    $domainName: String!
    $timezone: String!
    $country: String!
    $region: String!
    $postalCode: String!
  ) {
    updateUserDetails(
      companyName: $companyName
      domainName: $domainName
      firstName: $firstName
      lastName: $lastName
      timezone: $timezone
      country: $country
      region: $region
      postalCode: $postalCode
    ) {
      domainName
    }
  }
`;

export const FETCH_HISTORICAL_ENQUIRE_DATA = gql`
  query FetchHistoricalEnquireData {
    me {
      historicalEnquireDataFileNames
    }
  }
`;

export const UPDATE_ENQUIRE_REALTIME_DATA_SHEET_URL = gql`
  mutation UpdateEnquireRealtimeDataSheetUrl(
    $enquireRealtimeDataSheetUrl: String!
  ) {
    updateEnquireRealtimeDataSheetUrl(
      enquireRealtimeDataSheetUrl: $enquireRealtimeDataSheetUrl
    ) {
      enquireRealtimeDataSheetUrl
    }
  }
`;

export const SET_ENQUIRE_ATTRIBUTION_QUESTION_IDS = gql`
  mutation SetEnquireAttributionQuestionIds(
    $attributionQuestionIds: [String!]!
    $withReplacement: Boolean!
  ) {
    setEnquireAttributionQuestionIds(
      attributionQuestionIds: $attributionQuestionIds
      withReplacement: $withReplacement
    )
  }
`;

export const FETCH_ATTRIBUTION_QUESTION_IDS = gql`
  query FetchAttributionQuestionIds {
    me {
      attributionQuestionIds
    }
  }
`;

export const SET_SHOPIFY_SETTINGS = gql`
  mutation SetShopifySettings($shopUrl: String!, $collectPageViews: Boolean) {
    setShopifySettings(shopUrl: $shopUrl, collectPageViews: $collectPageViews)
  }
`;

export const FETCH_PIXEL_TRACKING_EVENTS = gql`
  query FetchPixelTrackingEvents(
    $types: [PixelTrackingEventType]
    $startDate: String!
    $endDate: String!
    $timeGranularity: TimeGranularityEnum!
  ) {
    me {
      pixelTrackingEvents(
        filter: {
          dateRange: { start: $startDate, end: $endDate }
          typeIn: $types
        }
      ) {
        summary(timeGranularity: $timeGranularity) {
          datetime
          values {
            byType {
              pageView {
                count
              }
              customGoal {
                count
              }
              purchaseEvent {
                count
              }
              identify {
                count
              }
              pagePing {
                count
              }
              purchaseEventError {
                count
              }
            }
            totalCount
          }
        }
        aggregated {
          totalCount
        }
        fpeErrorEvents {
          id
          action
          timestamp
          type
          rawData
          userIPAddress
          geoCountry
          userAgent
          pageURL
        }
        events {
          id
          action
          timestamp
          type
        }
      }
    }
  }
`;

export const FETCH_SERVER_API_SUMMARY = gql`
  query FetchServerAPISummaryTrackingEvents(
    $types: [ServerAPIServiceType]
    $startDate: String!
    $endDate: String!
    $timeGranularity: TimeGranularityEnum!
  ) {
    me {
      serverApiTrackingEvents(
        filter: {
          dateRange: { start: $startDate, end: $endDate }
          typeIn: $types
        }
      ) {
        summary(timeGranularity: $timeGranularity) {
          datetime
          values {
            byType {
              spendApi {
                count
              }
              ordersApi {
                count
              }
            }
            totalCount
          }
        }
      }
    }
  }
`;

export const FETCH_SERVER_API_TRACKING_EVENTS = gql`
  query FetchServerAPITrackingEvents(
    $types: [ServerAPIServiceType]
    $startDate: String!
    $endDate: String!
    $limit: Int
    $skip: Int
  ) {
    me {
      serverApiTrackingEvents(
        filter: {
          dateRange: { start: $startDate, end: $endDate }
          typeIn: $types
        }
      ) {
        events(limit: $limit, skip: $skip) {
          rows {
            message
            service
            error_type
            timestamp
            context
            request_id
          }
          totalCount
        }
      }
    }
  }
`;

export const FETCH_PIXEL_URL_CONFIGURATION = gql`
  query FetchPixelUrlConfiguration {
    me {
      domainName
      collectorSubdomain
    }
  }
`;
